import type { IHTTPParams } from '@/server/IServer';
import AJV from 'ajv';
import { path, tail } from 'ramda';
import { Either } from 'tsmonad';

import type { IAxiosRequesterConfig } from '../RequestService/requesters/AxiosRequester';

const ajv = !process.browser && new AJV({ coerceTypes: true, useDefaults: 'empty' });
const ajvStrict = !process.browser && new AJV({ coerceTypes: true, removeAdditional: true, useDefaults: 'empty' });

/**
 * 스키마와 객체를 받아 객체의 유효성을 검사한다. 단, `!data === true`일 경우 검사하지 않는다.
 * @param schema 검증 스키마 객체.
 * @param data 검증 대'상 객체.
 * @param strict `true`면 스키마가 정의하지 않은 필드를 제거.
 * @returns 유효하면 요청 매개변수를 담은 `Right`, 거짓이면 오류 배열을 담은 `Left`.
 */
export const validate: <T extends Partial<IHTTPParams> | undefined>(
  schema: Record<string, unknown>,
  data: T,
  strict?: boolean,
) => Either<AJV.ErrorObject[] | null | undefined, T> = process.browser
  ? (_, data) => Either.right(data)
  : (schema, data, strict) => {
      if (!data || !ajv || !ajvStrict) {
        return Either.right(data);
      }

      const runner = (strict ? ajvStrict : ajv).compile(schema);
      const valid = runner(data);

      if (valid) {
        return Either.right(data);
      }

      // 프로덕션 오류에서 개인정보 포함되는 것 방지
      const errors =
        process.env.RUN_MODE !== 'production' && runner.errors
          ? runner.errors.map((error) => {
              const dataPath = tail(error.dataPath).split('.');
              const input = path(dataPath, data);
              return {
                ...error,
                data: {
                  value: input,
                  type: typeof input,
                },
              };
            })
          : runner.errors;

      return Either.left(errors);
    };

/**
 * validate 함수의 Return Type
 */
export type TValidateReturn<T> = Either<AJV.ErrorObject[] | null | undefined, T>;

/**
 * `RequestBuilder`의 반환 형태.
 * @param L 오류 객체 유형. 언제나 유효해서 검사가 필요하지 않으면 `never` 사용.
 */
export type TReqConfigValidated<L = AJV.ErrorObject[] | undefined | null, R = IAxiosRequesterConfig> = Either<L, R>;
